import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Euro, FileStack, UserPlus, Wallet, RefreshCw, Megaphone, Mail,
  PhoneCall, Flame, ClipboardCheck, ArrowUpRight,
} from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, SectionLabel, StatCard, BarRow, MonthBars } from "@/components/admin/cc";
import { L, eur } from "@/lib/labels";
import { CallClicksPanel } from "@/components/admin/CallClicks";

const MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

const fmtAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "zojuist";
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} u`;
    return `${Math.floor(diff / 86400)} d`;
  } catch { return ""; }
};

export default function Dashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, e] = await Promise.all([
        api.get("/admin/stats", { headers: authHeaders() }),
        api.get("/admin/leads", { headers: authHeaders() }),
        api.get("/admin/emails", { headers: authHeaders(), params: { limit: 30 } }),
      ]);
      setStats(s.data); setLeads(l.data); setEmails(e.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally { setLoading(false); }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const monthData = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTHS[d.getMonth()], value: 0 });
    }
    const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
    leads.forEach((l) => {
      const d = new Date(l.created_at);
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      if (k in idx) buckets[idx[k]].value += 1;
    });
    return buckets;
  }, [leads]);

  const topTypes = (stats?.per_type || []).slice(0, 5).map((t) => ({ ...t, label: L.projectType(t.name) }));
  const maxType = Math.max(1, ...topTypes.map((t) => t.count));
  const byStatus = stats?.by_status || {};
  const byCat = stats?.by_category || {};
  const pipeline = [
    { label: "Nieuw", value: byStatus.nieuw || 0, tone: "accent" },
    { label: "Bezocht", value: byStatus.bezocht || 0, tone: "warn" },
    { label: "Offerte verzonden", value: byStatus.offerte_verzonden || 0, tone: "pos" },
    { label: "Afgerond", value: byStatus.afgerond || 0, tone: "pos" },
  ];
  const maxPipe = Math.max(1, ...pipeline.map((p) => p.value));

  const feed = useMemo(() => {
    const items = [];
    leads.slice(0, 8).forEach((l) => items.push({
      kind: "lead", ts: l.created_at, id: l.id, category: l.category,
      title: `${l.voornaam} ${l.achternaam}`,
      sub: `${(l.project_types || []).map(L.projectType).join(", ") || "Aanvraag"} · ${l.gemeente || "—"}`,
    }));
    emails.slice(0, 6).forEach((m) => items.push({
      kind: "email", ts: m.created_at, status: m.status,
      title: m.type === "campaign" ? "Campagne verstuurd" : m.type === "confirmation" ? "Bevestiging verstuurd" : "E-mail verstuurd",
      sub: m.subject,
    }));
    return items.sort((a, b) => new Date(b.ts) - new Date(a.ts)).slice(0, 9);
  }, [leads, emails]);

  const nieuwCount = byStatus.nieuw || 0;
  const bezochtCount = byStatus.bezocht || 0;
  const hotCount = byCat.hot || 0;
  const actionItems = [
    nieuwCount ? { tone: "accent", icon: PhoneCall, title: "Nieuwe leads opvolgen", text: `${nieuwCount} nieuwe aanvraag(en) nog niet bezocht`, go: "/admin/leads?status=nieuw" } : null,
    hotCount ? { tone: "hot", icon: Flame, title: "HOT leads — bel snel", text: `${hotCount} lead(s) met topprioriteit`, go: "/admin/leads?category=hot" } : null,
    bezochtCount ? { tone: "warn", icon: ClipboardCheck, title: "Offerte opmaken", text: `${bezochtCount} bezocht — klaar voor een offerte`, go: "/admin/leads?status=bezocht" } : null,
  ].filter(Boolean);

  const actions = (
    <>
      <button onClick={load} data-testid="dash-refresh" className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-cc-border px-3.5 py-2 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors">
        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Vernieuwen
      </button>
      <button onClick={() => navigate("/admin/marketing")} data-testid="dash-new-campaign" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-4 py-2 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors">
        <Megaphone className="w-4 h-4" /> Nieuwe campagne
      </button>
    </>
  );

  return (
    <AdminLayout title="Live Dashboard" actions={actions}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-pipeline" label="Pipelinewaarde" value={stats ? eur(stats.total_value) : "—"} sub="geschatte projectwaarde" subTone="pos" icon={Euro} />
        <StatCard testid="kpi-total" label="Totaal aanvragen" value={stats?.total ?? "—"} sub={`${nieuwCount} nieuw`} subTone="accent" icon={FileStack} />
        <StatCard testid="kpi-new" label="Nieuwe leads" value={nieuwCount} sub="te behandelen" icon={UserPlus} />
        <StatCard testid="kpi-avg" label="Gem. projectbudget" value={stats ? eur(stats.avg_budget) : "—"} sub={`${hotCount} HOT leads`} subTone="hot" icon={Wallet} />
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2 p-6">
          <SectionLabel right={<span className="font-heading font-extrabold text-xl text-cc-ink">{leads.length}<span className="text-cc-faint text-sm font-body font-medium ml-1">aanvragen</span></span>}>
            Aanvragen per maand
          </SectionLabel>
          <MonthBars data={monthData} />
        </Panel>

        <Panel className="p-6">
          <SectionLabel>Live feed</SectionLabel>
          {feed.length === 0 ? (
            <p className="font-body text-sm text-cc-faint py-6 text-center">Nog geen activiteit.</p>
          ) : (
            <div className="space-y-1.5 max-h-[232px] overflow-y-auto pr-1" data-testid="live-feed">
              {feed.map((f, i) => (
                <div
                  key={i}
                  onClick={() => f.kind === "lead" && f.id && navigate(`/admin/lead/${f.id}`)}
                  className={`flex items-start gap-3 rounded-xl px-3 py-2.5 ${f.kind === "lead" ? "cursor-pointer hover:bg-cc-panel2" : ""} transition-colors`}
                >
                  <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${f.kind === "lead" ? "bg-cc-accent/10 text-cc-accent" : "bg-cc-pos/10 text-cc-pos"}`}>
                    {f.kind === "lead" ? <UserPlus className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-body text-sm font-medium text-cc-ink truncate">{f.title}</p>
                    <p className="font-body text-xs text-cc-faint truncate">{f.sub}</p>
                  </div>
                  <span className="font-body text-[11px] text-cc-faint shrink-0">{fmtAgo(f.ts)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel className="p-6">
          <SectionLabel>Top projecttypes</SectionLabel>
          {topTypes.length === 0 ? (
            <p className="font-body text-sm text-cc-faint">Geen data.</p>
          ) : (
            <div data-testid="top-types">
              {topTypes.map((t) => <BarRow key={t.name} label={t.label} value={t.count} max={maxType} />)}
            </div>
          )}
        </Panel>

        <Panel className="p-6">
          <SectionLabel>Pipeline (status)</SectionLabel>
          <div data-testid="pipeline">
            {pipeline.map((p) => <BarRow key={p.label} label={p.label} value={p.value} max={maxPipe} tone={p.tone} />)}
          </div>
        </Panel>

        <Panel className="p-6">
          <SectionLabel right={<span className="font-body text-[11px] font-bold text-cc-faint">{actionItems.length} open</span>}>Actiepunten</SectionLabel>
          {actionItems.length === 0 ? (
            <p className="font-body text-sm text-cc-faint py-4">Alles opgevolgd.</p>
          ) : (
            <div className="space-y-2.5" data-testid="action-items">
              {actionItems.map((a, i) => {
                const Icon = a.icon;
                const toneBg = a.tone === "hot" ? "bg-cc-hot/10 text-cc-hot" : a.tone === "warn" ? "bg-cc-warn/10 text-cc-warn" : "bg-cc-accent/10 text-cc-accent";
                return (
                  <button key={i} onClick={() => navigate(a.go)} data-testid={`action-item-${i}`} className="w-full flex items-center gap-3 rounded-xl border border-cc-border bg-cc-panel2/50 px-3.5 py-3 text-left hover:border-cc-accent/60 transition-colors group">
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0 ${toneBg}`}><Icon className="w-4 h-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-sm font-semibold text-cc-ink truncate">{a.title}</p>
                      <p className="font-body text-xs text-cc-faint truncate">{a.text}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-cc-faint group-hover:text-cc-accent transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
      <CallClicksPanel />
    </AdminLayout>
  );
}
